import React, { useState, useEffect, useMemo } from 'react';
import {
  alpha,
  Grid,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stack,
  Button,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Flight,
  AirplanemodeActive,
  Build,
  Warning,
  CheckCircle,
  PendingActions,
  Inventory2,
  Refresh,
  Dashboard as DashboardIcon,
  Timeline,
  PieChart as PieChartIcon
} from '@mui/icons-material';
import axios from 'axios';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { ROLES, isFlightCrewRole } from '../constants/roles';
import { hasSuperAdminView, roleMatches } from '../utils/rolePermissions';
import { needsStoreIntentAction } from '../utils/workOrderStoreReview';
import DepartmentWiseAnalysis from './dashboard/DepartmentWiseAnalysis';

const THEME = {
  ink: '#0b1f33',
  navy: '#132a43',
  steel: '#3d5a80',
  mist: '#e8eef5',
  paper: '#f4f7fb',
  card: '#ffffff',
  border: '#d5dee9',
  gold: '#9a7b4f',
  goldSoft: '#c4a574',
  success: '#1b6b4a',
  warning: '#a16207',
  danger: '#9b2c2c',
  info: '#1d4f7c',
  muted: '#5c6b7a'
};

const formatUpdatedAt = (date) => {
  if (!date) return 'Never';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
    hour12: false
  }).format(date);
};

const ChartTooltipContent = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 1,
        border: `1px solid ${THEME.border}`,
        bgcolor: THEME.card,
        borderRadius: 1.5,
        boxShadow: `0 8px 24px ${alpha(THEME.ink, 0.12)}`
      }}
    >
      <Typography variant="caption" sx={{ color: THEME.muted, display: 'block' }}>
        {item.name || item.payload?.name}
      </Typography>
      <Typography variant="subtitle2" sx={{ color: THEME.ink, fontWeight: 700 }}>
        {item.value}
      </Typography>
    </Paper>
  );
};

const SummaryMetric = ({ label, value, hint, tone = 'navy', icon }) => {
  const toneColor =
    tone === 'gold'
      ? THEME.gold
      : tone === 'success'
        ? THEME.success
        : tone === 'warning'
          ? THEME.warning
          : tone === 'info'
            ? THEME.info
            : THEME.navy;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        height: '100%',
        borderRadius: 2.5,
        border: `1px solid ${THEME.border}`,
        background: `linear-gradient(145deg, ${THEME.card} 0%, ${alpha(toneColor, 0.06)} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 10px 28px ${alpha(THEME.ink, 0.08)}`
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: toneColor
        }
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Typography
          variant="overline"
          sx={{ color: THEME.muted, letterSpacing: 1.2, fontWeight: 600, lineHeight: 1.2 }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.25,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(toneColor, 0.12),
            color: toneColor
          }}
        >
          {icon}
        </Box>
      </Stack>
      <Typography
        variant="h3"
        sx={{
          mt: 1,
          mb: 0.5,
          color: THEME.ink,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.02em'
        }}
      >
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: THEME.muted }}>
        {hint}
      </Typography>
    </Paper>
  );
};

const QueueTile = ({ label, value, unit, accent = THEME.steel }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.75,
      height: '100%',
      borderRadius: 2,
      border: `1px solid ${alpha(accent, 0.28)}`,
      background: `linear-gradient(160deg, ${THEME.card} 0%, ${alpha(accent, 0.07)} 100%)`,
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: `0 8px 20px ${alpha(THEME.ink, 0.06)}`
      }
    }}
  >
    <Typography variant="body2" sx={{ color: THEME.muted, mb: 0.5, fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography
      variant="h4"
      sx={{ color: accent, fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}
    >
      {value}
    </Typography>
    <Typography variant="caption" sx={{ color: THEME.muted }}>
      {unit}
    </Typography>
  </Paper>
);

const SectionCard = ({ title, icon, children, subtitle }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      mb: 3,
      borderRadius: 2.5,
      border: `1px solid ${THEME.border}`,
      bgcolor: THEME.card
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: subtitle ? 0.5 : 2 }}>
      {icon}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink }}>
        {title}
      </Typography>
    </Stack>
    {subtitle && (
      <Typography variant="body2" sx={{ color: THEME.muted, mb: 2 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Paper>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalFlights: 0,
    activeFlights: 0,
    totalAircraft: 0,
    availableAircraft: 0,
    pendingMaintenance: 0
  });
  const [recentFlights, setRecentFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState({
    amApprovals: 0,
    camoApprovals: 0,
    camoQmAcknowledgments: 0,
    planningApprovals: 0
  });
  const [pendingActions, setPendingActions] = useState({
    baTests: 0,
    atcClearances: 0,
    storeActions: 0,
    mroActions: 0,
    mroEngineerCompletion: 0
  });
  const [storeAlerts, setStoreAlerts] = useState({
    lowStock: [],
    expiringItems: [],
    calibrationDue: []
  });
  const [deptAnalysisSource, setDeptAnalysisSource] = useState({
    trips: [],
    sectors: [],
    maintenance: [],
    purchaseOrders: [],
    receiving: [],
    intimations: [],
    technicalLogBooks: [],
    aircraft: []
  });
  const [deptAnalysisLoading, setDeptAnalysisLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const isCrew = isFlightCrewRole(user?.role);
      const canViewTrips = roleMatches(user?.role, [
        ROLES.SALES,
        ROLES.OPERATIONS_MANAGER,
        ROLES.OPERATIONS_EXECUTIVE,
        ROLES.ACCOUNTABLE_MANAGER,
        ROLES.SUPER_ADMIN
      ]);
      const canViewAircraft = roleMatches(user?.role, [
        ROLES.SUPER_ADMIN,
        ROLES.OPERATIONS_MANAGER,
        ROLES.OPERATIONS_EXECUTIVE,
        ROLES.ACCOUNTABLE_MANAGER,
        ROLES.CA_MANAGER,
        ROLES.MRO_MANAGER,
        ROLES.MRO_ENGINEER,
        ROLES.MRO_QM_MANAGER,
        ROLES.MRO_QM_OFFICER
      ]);
      const canViewMaintenance = roleMatches(user?.role, [
        ROLES.SUPER_ADMIN,
        ROLES.OPERATIONS_MANAGER,
        ROLES.OPERATIONS_EXECUTIVE,
        ROLES.ACCOUNTABLE_MANAGER,
        ROLES.CA_MANAGER,
        ROLES.CA_TECHNICAL_OFFICER, ROLES.PLANNING_MANAGER,
        ROLES.MRO_MANAGER
      ]);
      const canViewSectorsFetch = roleMatches(user?.role, [
        ROLES.SUPER_ADMIN,
        ROLES.OPERATIONS_MANAGER,
        ROLES.OPERATIONS_EXECUTIVE,
        ROLES.ACCOUNTABLE_MANAGER
      ]);
      const canViewStoreFetch = roleMatches(user?.role, [
        ROLES.SUPER_ADMIN,
        ROLES.STORE_INSPECTOR,
        ROLES.CA_MANAGER,
        ROLES.MRO_MANAGER,
        ROLES.CAMO_QM_MANAGER,
        ROLES.ACCOUNTABLE_MANAGER
      ]);

      const promises = [];

      if (isCrew) {
        promises.push(
          axios.get('/api/crew/my-assignments', { headers }).catch((err) => {
            if (err.response?.status !== 403) {
              console.error('Error fetching crew assignments:', err);
            }
            return { data: [] };
          })
        );
      } else if (canViewTrips) {
        promises.push(
          axios.get('/api/sales/trips', { headers }).catch((err) => {
            if (err.response?.status !== 403) {
              console.error('Error fetching trips:', err);
            }
            return { data: [] };
          })
        );
      } else {
        promises.push(Promise.resolve({ data: [] }));
      }

      if (canViewAircraft) {
        promises.push(
          axios.get('/api/aircraft', { headers }).catch((err) => {
            if (err.response?.status !== 403) {
              console.error('Error fetching aircraft:', err);
            }
            return { data: [] };
          })
        );
      } else {
        promises.push(Promise.resolve({ data: [] }));
      }

      if (canViewMaintenance) {
        promises.push(
          axios.get('/api/maintenance', { headers }).catch((err) => {
            if (err.response?.status !== 403) {
              console.error('Error fetching maintenance:', err);
            }
            return { data: [] };
          })
        );
      } else {
        promises.push(Promise.resolve({ data: [] }));
      }

      if (canViewSectorsFetch) {
        promises.push(
          axios.get('/api/ops/sectors', { headers, params: { summary: '1' } }).catch((err) => {
            if (err.response?.status !== 403) {
              console.error('Error fetching sectors:', err);
            }
            return { data: [] };
          })
        );
      } else {
        promises.push(Promise.resolve({ data: [] }));
      }

      if (canViewStoreFetch) {
        promises.push(
          axios.get('/api/store/inventory/low-stock', { headers }).catch((err) => {
            if (err.response?.status !== 403) {
              console.error('Error fetching low stock items:', err);
            }
            return { data: [] };
          })
        );
        promises.push(
          axios.get('/api/store/inventory/expiring', { headers }).catch((err) => {
            if (err.response?.status !== 403) {
              console.error('Error fetching expiring items:', err);
            }
            return { data: [] };
          })
        );
        promises.push(
          axios.get('/api/store/inventory/tools-calibration-due', { headers }).catch((err) => {
            if (err.response?.status !== 403) {
              console.error('Error fetching tool calibration due (inventory):', err);
            }
            return { data: [] };
          })
        );
      } else {
        promises.push(Promise.resolve({ data: [] }));
        promises.push(Promise.resolve({ data: [] }));
        promises.push(Promise.resolve({ data: [] }));
      }

      const [
        tripsOrAssignmentsRes,
        aircraftRes,
        maintenanceRes,
        sectorsRes,
        lowStockRes,
        expiringItemsRes,
        calibrationDueRes
      ] = await Promise.all(promises);

      const tripsOrAssignments = tripsOrAssignmentsRes.data;
      const aircraft = aircraftRes.data;
      const maintenance = maintenanceRes.data;
      const sectors = sectorsRes?.data || [];
      const lowStockItems = lowStockRes?.data || [];
      const expiringItems = expiringItemsRes?.data || [];
      const calibrationDueTools = calibrationDueRes?.data || [];
      const isSuperAdmin = hasSuperAdminView(user?.role);

      // Reset role-scoped counters so stale values do not linger across refresh/role changes
      setPendingApprovals({
        amApprovals: 0,
        camoApprovals: 0,
        camoQmAcknowledgments: 0,
        planningApprovals: 0
      });
      setPendingActions({
        baTests: 0,
        atcClearances: 0,
        storeActions: 0,
        mroActions: 0,
        mroEngineerCompletion: 0
      });
      setStoreAlerts({ lowStock: [], expiringItems: [], calibrationDue: [] });
      setDeptAnalysisSource({
        trips: [],
        sectors: [],
        maintenance: [],
        purchaseOrders: [],
        receiving: [],
        intimations: [],
        technicalLogBooks: [],
        aircraft: []
      });
      setDeptAnalysisLoading(false);

      if (isCrew) {
        const assignments = tripsOrAssignments;
        const uniqueTrips = [...new Set(assignments.map((a) => a.tripId?._id).filter(Boolean))];

        setStats({
          totalFlights: uniqueTrips.length,
          activeFlights: assignments.filter((a) =>
            ['ASSIGNED', 'CREW_ACCEPTED', 'READY_FOR_BA', 'READY_FOR_ATC', 'CLEARED'].includes(a.status)
          ).length,
          totalAircraft: 0,
          availableAircraft: 0,
          pendingMaintenance: 0
        });

        setRecentFlights(
          assignments.slice(0, 5).map((a) => ({
            _id: a._id,
            charterNumber: a.tripId?.charterNumber || 'N/A',
            title: a.tripId?.title || 'Assignment',
            flightType: a.tripId?.flightType || 'CHARTER',
            status: a.status
          }))
        );
      } else {
        const trips = tripsOrAssignments;

        const activeTrips = trips.filter(
          (t) => !['DRAFT', 'CANCELLED', 'COMPLETED', 'CHARTER_COMPLETED'].includes(t.status)
        );

        const inProgressTrips = trips.filter((t) =>
          ['CREW_ASSIGNMENT', 'READY_FOR_BA', 'READY_FOR_ATC'].includes(t.status)
        );

        const scheduledMaintenance = maintenance.filter((m) => m.status === 'SCHEDULED');
        const uniqueAircraftNeedingMaintenance = new Set(
          scheduledMaintenance.map((m) => m.aircraftId?.toString()).filter(Boolean)
        );

        setStats({
          totalFlights: activeTrips.length,
          activeFlights: inProgressTrips.length,
          totalAircraft: aircraft.length,
          availableAircraft: aircraft.filter((a) => a.status === 'AVAILABLE').length,
          pendingMaintenance: uniqueAircraftNeedingMaintenance.size
        });

        setRecentFlights(activeTrips.slice(0, 5));

        if (canViewTrips) {
          const tripsNeedingAMApproval = trips.filter(
            (t) =>
              t.status === 'CREW_ASSIGNMENT' && t.approvals?.accountableManager?.approved !== true
          );

          const tripsNeedingCAMOApproval = trips.filter(
            (t) =>
              t.status === 'CREW_ASSIGNMENT' &&
              t.approvals?.accountableManager?.approved === true &&
              t.approvals?.camo?.approved !== true
          );

          setPendingApprovals((prev) => ({
            ...prev,
            amApprovals: tripsNeedingAMApproval.length,
            camoApprovals: tripsNeedingCAMOApproval.length
          }));
        }

        if (canViewMaintenance) {
          const maintenanceNeedingCAMOQM = maintenance.filter(
            (m) => m.status === 'SCHEDULED' && m.camoQmAcknowledged !== true
          );

          const maintenanceNeedingPlanning = maintenance.filter(
            (m) =>
              m.status === 'SCHEDULED' &&
              m.camoQmAcknowledged === true &&
              m.planningApproved !== true
          );

          setPendingApprovals((prev) => ({
            ...prev,
            camoQmAcknowledgments: maintenanceNeedingCAMOQM.length,
            planningApprovals: maintenanceNeedingPlanning.length
          }));

          const maintenanceNeedingStore = maintenance.filter(
            (m) =>
              m.status === 'SCHEDULED' &&
              m.planningApproved === true &&
              needsStoreIntentAction(m)
          );

          const maintenanceNeedingMRO = maintenance.filter(
            (m) =>
              m.status === 'SCHEDULED' &&
              m.planningApproved === true &&
              m.needsMROAction === true &&
              !m.mroAccepted
          );

          const maintenanceNeedingEngineer = maintenance.filter((m) => m.status === 'IN_PROGRESS');

          setPendingActions((prev) => ({
            ...prev,
            storeActions: maintenanceNeedingStore.length,
            mroActions: maintenanceNeedingMRO.length,
            mroEngineerCompletion: maintenanceNeedingEngineer.length
          }));
        }

        if (canViewSectorsFetch) {
          const sectorsNeedingBA = sectors.filter((s) => s.status === 'READY_FOR_BA');
          const sectorsNeedingATC = sectors.filter((s) => s.status === 'READY_FOR_ATC');

          setPendingActions((prev) => ({
            ...prev,
            baTests: sectorsNeedingBA.length,
            atcClearances: sectorsNeedingATC.length
          }));
        }

        if (canViewStoreFetch) {
          setStoreAlerts({
            lowStock: lowStockItems,
            expiringItems: expiringItems,
            calibrationDue: calibrationDueTools
          });
        }

        if (isSuperAdmin) {
          const baseTrips = Array.isArray(trips) ? trips : [];
          const baseSectors = Array.isArray(sectors) ? sectors : [];
          const baseMaintenance = Array.isArray(maintenance) ? maintenance : [];
          const baseAircraft = Array.isArray(aircraft) ? aircraft : [];

          // Show KPIs immediately; load heavy dept-analysis sources in the background.
          setDeptAnalysisSource({
            trips: baseTrips,
            sectors: baseSectors,
            maintenance: baseMaintenance,
            purchaseOrders: [],
            receiving: [],
            intimations: [],
            technicalLogBooks: [],
            aircraft: baseAircraft
          });
          setDeptAnalysisLoading(true);

          Promise.all([
            axios.get('/api/store/purchase-orders', { headers }).catch((err) => {
              if (err.response?.status !== 403) {
                console.error('Error fetching purchase orders:', err);
              }
              return { data: [] };
            }),
            axios.get('/api/store/receiving', { headers }).catch((err) => {
              if (err.response?.status !== 403) {
                console.error('Error fetching receiving records:', err);
              }
              return { data: [] };
            }),
            axios.get('/api/intimations', { headers }).catch((err) => {
              if (err.response?.status !== 403) {
                console.error('Error fetching intimations:', err);
              }
              return { data: [] };
            }),
            axios.get('/api/technical-log-book', { headers, params: { summary: '1' } }).catch((err) => {
              if (err.response?.status !== 403) {
                console.error('Error fetching technical log books:', err);
              }
              return { data: [] };
            })
          ])
            .then(([purchaseOrdersRes, receivingRes, intimationsRes, technicalLogBooksRes]) => {
                const purchaseOrders = Array.isArray(purchaseOrdersRes?.data)
                  ? purchaseOrdersRes.data
                  : purchaseOrdersRes?.data?.items ||
                    purchaseOrdersRes?.data?.purchaseOrders ||
                    [];
                const receiving = Array.isArray(receivingRes?.data)
                  ? receivingRes.data
                  : receivingRes?.data?.items || receivingRes?.data?.records || [];
                const intimations = Array.isArray(intimationsRes?.data)
                  ? intimationsRes.data
                  : [];
                const technicalLogBooks = Array.isArray(technicalLogBooksRes?.data)
                  ? technicalLogBooksRes.data
                  : [];

                setDeptAnalysisSource({
                  trips: baseTrips,
                  sectors: baseSectors,
                  maintenance: baseMaintenance,
                  purchaseOrders,
                  receiving,
                  intimations,
                  technicalLogBooks,
                  aircraft: baseAircraft
                });
              }
            )
            .catch((err) => {
              console.error('Error loading department analysis data:', err);
            })
            .finally(() => {
              setDeptAnalysisLoading(false);
            });
        }
      }

      setLastUpdated(new Date());
      setLoading(false);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching dashboard data:', error);
      }
      setLastUpdated(new Date());
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'default';
      case 'PENDING_OPS':
        return 'warning';
      case 'CREW_ASSIGNMENT':
        return 'info';
      case 'ASSIGNED':
        return 'info';
      case 'CREW_ACCEPTED':
        return 'success';
      case 'CREW_REJECTED':
        return 'error';
      case 'READY_FOR_BA':
        return 'warning';
      case 'READY_FOR_ATC':
        return 'info';
      case 'CLEARED':
        return 'info';
      case 'COMPLETED':
        return 'success';
      case 'SECTOR_COMPLETED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const canViewSectors = roleMatches(user?.role, [
    ROLES.SUPER_ADMIN,
    ROLES.OPERATIONS_MANAGER,
    ROLES.OPERATIONS_EXECUTIVE,
    ROLES.ACCOUNTABLE_MANAGER
  ]);
  const canViewStore = roleMatches(user?.role, [
    ROLES.SUPER_ADMIN,
    ROLES.STORE_INSPECTOR,
    ROLES.CA_MANAGER,
    ROLES.MRO_MANAGER,
    ROLES.CAMO_QM_MANAGER,
    ROLES.ACCOUNTABLE_MANAGER
  ]);
  const isCrew = isFlightCrewRole(user?.role);

  const visibleApprovals = useMemo(() => {
    const items = [];
    if (
      pendingApprovals.amApprovals > 0 &&
      roleMatches(user?.role, [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER])
    ) {
      items.push({
        key: 'am',
        label: 'AM Approvals',
        value: pendingApprovals.amApprovals,
        unit: 'trips',
        accent: THEME.gold
      });
    }
    if (
      pendingApprovals.camoApprovals > 0 &&
      roleMatches(user?.role, [ROLES.SUPER_ADMIN, ROLES.CA_MANAGER])
    ) {
      items.push({
        key: 'camo',
        label: 'CAMO Approvals',
        value: pendingApprovals.camoApprovals,
        unit: 'trips',
        accent: THEME.steel
      });
    }
    if (
      pendingApprovals.camoQmAcknowledgments > 0 &&
      roleMatches(user?.role, [ROLES.SUPER_ADMIN, ROLES.CAMO_QM_MANAGER])
    ) {
      items.push({
        key: 'camoQm',
        label: 'CAMO QM Acknowledgments',
        value: pendingApprovals.camoQmAcknowledgments,
        unit: 'work orders',
        accent: THEME.warning
      });
    }
    if (
      pendingApprovals.planningApprovals > 0 &&
      roleMatches(user?.role, [ROLES.SUPER_ADMIN, ROLES.PLANNING_MANAGER])
    ) {
      items.push({
        key: 'planning',
        label: 'Planning Approvals',
        value: pendingApprovals.planningApprovals,
        unit: 'work orders',
        accent: THEME.info
      });
    }
    return items;
  }, [pendingApprovals, user?.role]);

  const visibleActions = useMemo(() => {
    const items = [];
    if (pendingActions.baTests > 0 && canViewSectors) {
      items.push({
        key: 'ba',
        label: 'BA Tests',
        value: pendingActions.baTests,
        unit: 'sectors',
        accent: THEME.warning
      });
    }
    if (pendingActions.atcClearances > 0 && canViewSectors) {
      items.push({
        key: 'atc',
        label: 'ATC Clearances',
        value: pendingActions.atcClearances,
        unit: 'sectors',
        accent: THEME.info
      });
    }
    if (
      pendingActions.storeActions > 0 &&
      roleMatches(user?.role, [ROLES.SUPER_ADMIN, ROLES.STORE_INSPECTOR])
    ) {
      items.push({
        key: 'store',
        label: 'Store Actions',
        value: pendingActions.storeActions,
        unit: 'work orders',
        accent: '#6b5b3e'
      });
    }
    if (
      pendingActions.mroActions > 0 &&
      roleMatches(user?.role, [ROLES.SUPER_ADMIN, ROLES.MRO_MANAGER, ROLES.PLANNING_MANAGER])
    ) {
      items.push({
        key: 'mro',
        label: 'MRO Actions',
        value: pendingActions.mroActions,
        unit: 'work orders',
        accent: THEME.steel
      });
    }
    if (
      pendingActions.mroEngineerCompletion > 0 &&
      roleMatches(user?.role, [ROLES.SUPER_ADMIN, ROLES.MRO_ENGINEER, ROLES.MRO_MANAGER])
    ) {
      items.push({
        key: 'engineer',
        label: 'MRO Engineer Completion',
        value: pendingActions.mroEngineerCompletion,
        unit: 'work orders',
        accent: THEME.success
      });
    }
    return items;
  }, [pendingActions, canViewSectors, user?.role]);

  const storeAlertTiles = useMemo(() => {
    const items = [];
    if (storeAlerts.lowStock.length > 0) {
      items.push({
        key: 'low',
        label: 'Low Stock Items',
        value: storeAlerts.lowStock.length,
        unit: 'below reorder point',
        accent: THEME.warning
      });
    }
    if (storeAlerts.expiringItems.length > 0) {
      items.push({
        key: 'expiring',
        label: 'Items Expiring Soon',
        value: storeAlerts.expiringItems.length,
        unit: 'within 30 days',
        accent: THEME.danger
      });
    }
    if (storeAlerts.calibrationDue.length > 0) {
      items.push({
        key: 'cal',
        label: 'Tool Calibration Due',
        value: storeAlerts.calibrationDue.length,
        unit: 'due within 30 days or overdue',
        accent: THEME.danger
      });
    }
    return items;
  }, [storeAlerts]);

  const compositionData = useMemo(() => {
    const approvalTotal = visibleApprovals.reduce((sum, i) => sum + i.value, 0);
    const actionTotal = visibleActions.reduce((sum, i) => sum + i.value, 0);
    const storeTotal = storeAlertTiles.reduce((sum, i) => sum + i.value, 0);
    return [
      { name: 'Pending approvals', value: approvalTotal, color: THEME.gold },
      { name: 'Pending actions', value: actionTotal, color: THEME.steel },
      { name: 'Store alerts', value: storeTotal, color: THEME.warning }
    ].filter((d) => d.value > 0);
  }, [visibleApprovals, visibleActions, storeAlertTiles]);

  const queueBarData = useMemo(() => {
    return [
      ...visibleApprovals.map((i) => ({ name: i.label, value: i.value, fill: i.accent })),
      ...visibleActions.map((i) => ({ name: i.label, value: i.value, fill: i.accent })),
      ...storeAlertTiles.map((i) => ({ name: i.label, value: i.value, fill: i.accent }))
    ];
  }, [visibleApprovals, visibleActions, storeAlertTiles]);

  const fleetAvailabilityPct =
    stats.totalAircraft > 0
      ? Math.round((stats.availableAircraft / stats.totalAircraft) * 100)
      : null;

  const compositionTotal = compositionData.reduce((sum, d) => sum + d.value, 0);

  const systemAlerts = [];
  if (
    stats.pendingMaintenance > 0 &&
    roleMatches(user?.role, [
      ROLES.SUPER_ADMIN,
      ROLES.OPERATIONS_MANAGER,
      ROLES.OPERATIONS_EXECUTIVE,
      ROLES.ACCOUNTABLE_MANAGER,
      ROLES.CA_MANAGER,
      ROLES.MRO_MANAGER
    ])
  ) {
    systemAlerts.push({
      key: 'maint',
      tone: 'warning',
      title: 'Maintenance Required',
      detail: `${stats.pendingMaintenance} aircraft require maintenance`
    });
  }
  if (
    stats.availableAircraft === 0 &&
    stats.totalAircraft > 0 &&
    roleMatches(user?.role, [
      ROLES.SUPER_ADMIN,
      ROLES.OPERATIONS_MANAGER,
      ROLES.OPERATIONS_EXECUTIVE,
      ROLES.ACCOUNTABLE_MANAGER,
      ROLES.CA_MANAGER,
      ROLES.MRO_MANAGER,
      ROLES.SALES
    ])
  ) {
    systemAlerts.push({
      key: 'fleet',
      tone: 'danger',
      title: 'No Available Aircraft',
      detail: 'All aircraft are currently unavailable'
    });
  }
  if (isCrew && stats.activeFlights > 0) {
    systemAlerts.push({
      key: 'crew',
      tone: 'info',
      title: 'Active Assignments',
      detail: `You have ${stats.activeFlights} active assignment(s) requiring your attention`
    });
  }

  return (
    <Box
      sx={{
        mx: { xs: -1, sm: -2 },
        mb: -2,
        px: { xs: 1.5, sm: 2.5 },
        pb: 3,
        pt: 0.5,
        minHeight: '100%',
        background: `
          radial-gradient(ellipse 80% 50% at 100% -10%, ${alpha(THEME.gold, 0.12)} 0%, transparent 55%),
          radial-gradient(ellipse 60% 40% at 0% 0%, ${alpha(THEME.steel, 0.1)} 0%, transparent 50%),
          linear-gradient(180deg, ${THEME.paper} 0%, #eef2f7 100%)
        `
      }}
    >
      {/* Hero */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${THEME.border}`,
          background: `linear-gradient(125deg, ${THEME.ink} 0%, ${THEME.navy} 48%, ${THEME.steel} 100%)`,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `
              linear-gradient(90deg, transparent 60%, ${alpha(THEME.gold, 0.15)} 100%),
              repeating-linear-gradient(
                -12deg,
                transparent,
                transparent 18px,
                ${alpha('#ffffff', 0.015)} 18px,
                ${alpha('#ffffff', 0.015)} 19px
              )
            `,
            pointerEvents: 'none'
          }
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ p: { xs: 2.5, md: 3 }, position: 'relative', zIndex: 1 }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.25,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(THEME.gold, 0.2),
                  color: THEME.goldSoft,
                  border: `1px solid ${alpha(THEME.goldSoft, 0.35)}`
                }}
              >
                <DashboardIcon fontSize="small" />
              </Box>
              <Chip
                size="small"
                label={user?.role ? String(user.role).replace(/_/g, ' ') : 'Operations'}
                sx={{
                  bgcolor: alpha('#ffffff', 0.1),
                  color: alpha('#ffffff', 0.9),
                  border: `1px solid ${alpha('#ffffff', 0.18)}`,
                  fontWeight: 600,
                  letterSpacing: 0.4
                }}
              />
            </Stack>
            <Typography
              variant="h4"
              sx={{ color: '#fff', fontWeight: 700, letterSpacing: '-0.02em', mb: 0.75 }}
            >
              {hasSuperAdminView(user?.role)
                ? 'Fleetwright Operations Overview'
                : 'Operations Overview'}
            </Typography>
            <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.72), maxWidth: 560 }}>
              {hasSuperAdminView(user?.role)
                ? 'Fleet status, department performance, and role-relevant queues in one place.'
                : 'Fleet status, flight activity, and role-relevant queues in one place.'}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Box sx={{ textAlign: { xs: 'left', md: 'right' }, mr: { md: 1 } }}>
              <Typography variant="caption" sx={{ color: alpha('#ffffff', 0.55), display: 'block' }}>
                Last refreshed
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.9), fontWeight: 600 }}>
                {formatUpdatedAt(lastUpdated)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
              onClick={fetchDashboardData}
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: THEME.gold,
                color: THEME.ink,
                boxShadow: 'none',
                px: 2.25,
                '&:hover': { bgcolor: THEME.goldSoft, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: alpha(THEME.gold, 0.4), color: alpha(THEME.ink, 0.5) }
              }}
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {loading && !lastUpdated ? (
        <Box sx={{ py: 10, display: 'grid', placeItems: 'center' }}>
          <CircularProgress sx={{ color: THEME.steel }} />
        </Box>
      ) : (
        <>
          {/* KPI strip */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryMetric
                label="Upcoming Flights"
                value={stats.totalFlights}
                hint={isCrew ? 'Assigned trips' : 'Active trips excluding drafts & closed'}
                tone="navy"
                icon={<Flight fontSize="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryMetric
                label="Flights in Progress"
                value={stats.activeFlights}
                hint={isCrew ? 'Assignments needing attention' : 'Crew, BA, or ATC stages'}
                tone="success"
                icon={<Flight fontSize="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryMetric
                label="Available Aircraft"
                value={`${stats.availableAircraft}/${stats.totalAircraft}`}
                hint={
                  fleetAvailabilityPct != null
                    ? `${fleetAvailabilityPct}% of fleet available`
                    : 'Fleet visibility for your role'
                }
                tone="info"
                icon={<AirplanemodeActive fontSize="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryMetric
                label="Maintenance Required"
                value={stats.pendingMaintenance}
                hint="Unique aircraft with scheduled work"
                tone="warning"
                icon={<Build fontSize="small" />}
              />
            </Grid>
          </Grid>

          {hasSuperAdminView(user?.role) && (
            <DepartmentWiseAnalysis
              trips={deptAnalysisSource.trips}
              sectors={deptAnalysisSource.sectors}
              maintenance={deptAnalysisSource.maintenance}
              purchaseOrders={deptAnalysisSource.purchaseOrders}
              receiving={deptAnalysisSource.receiving}
              intimations={deptAnalysisSource.intimations}
              technicalLogBooks={deptAnalysisSource.technicalLogBooks}
              aircraft={deptAnalysisSource.aircraft}
              storeAlerts={storeAlerts}
              analysisLoading={deptAnalysisLoading}
            />
          )}

          {/* Charts — only when there is queue/alert data */}
          {(compositionData.length > 0 || queueBarData.length > 0) && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} lg={5}>
                <Paper
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: 2.5,
                    borderRadius: 2.5,
                    border: `1px solid ${THEME.border}`,
                    bgcolor: THEME.card
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <PieChartIcon sx={{ color: THEME.gold, fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink }}>
                      Attention mix
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: THEME.muted, mb: 2 }}>
                    Approvals, operational actions, and store alerts visible to your role.
                  </Typography>
                  {compositionData.length === 0 ? (
                    <Box
                      sx={{
                        height: 260,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 2,
                        bgcolor: alpha(THEME.success, 0.06),
                        border: `1px dashed ${alpha(THEME.success, 0.35)}`
                      }}
                    >
                      <Typography variant="body2" sx={{ color: THEME.muted }}>
                        No attention items for your role.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ height: 260, position: 'relative' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={compositionData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="46%"
                            innerRadius={62}
                            outerRadius={92}
                            paddingAngle={3}
                            stroke={THEME.card}
                            strokeWidth={3}
                          >
                            {compositionData.map((entry) => (
                              <Cell key={entry.name} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip content={<ChartTooltipContent />} />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            formatter={(value) => (
                              <span style={{ color: THEME.muted, fontSize: 12 }}>{value}</span>
                            )}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '42%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          textAlign: 'center',
                          pointerEvents: 'none'
                        }}
                      >
                        <Typography variant="caption" sx={{ color: THEME.muted, display: 'block' }}>
                          Total
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{ color: THEME.ink, fontWeight: 700, lineHeight: 1.1 }}
                        >
                          {compositionTotal}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} lg={7}>
                <Paper
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: 2.5,
                    borderRadius: 2.5,
                    border: `1px solid ${THEME.border}`,
                    bgcolor: THEME.card
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Timeline sx={{ color: THEME.steel, fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink }}>
                      Queue depth by area
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: THEME.muted, mb: 2 }}>
                    Active queues only — empty lanes are omitted.
                  </Typography>
                  {queueBarData.length === 0 ? (
                    <Box
                      sx={{
                        height: 260,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 2,
                        bgcolor: THEME.mist
                      }}
                    >
                      <Typography variant="body2" sx={{ color: THEME.muted }}>
                        No active queues to chart.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ height: Math.max(220, queueBarData.length * 36), minHeight: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={queueBarData}
                          layout="vertical"
                          margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={THEME.border}
                            horizontal={false}
                          />
                          <XAxis
                            type="number"
                            allowDecimals={false}
                            tick={{ fill: THEME.muted, fontSize: 11 }}
                            axisLine={{ stroke: THEME.border }}
                            tickLine={false}
                          />
                          <YAxis
                            type="category"
                            dataKey="name"
                            width={140}
                            tick={{ fill: THEME.ink, fontSize: 11, fontWeight: 500 }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <RechartsTooltip
                            content={<ChartTooltipContent />}
                            cursor={{ fill: alpha(THEME.steel, 0.06) }}
                          />
                          <Bar dataKey="value" name="Items" radius={[0, 6, 6, 0]} maxBarSize={22} barSize={18}>
                            {queueBarData.map((entry) => (
                              <Cell key={entry.name} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Pending Approvals */}
          {visibleApprovals.length > 0 && (
            <SectionCard
              title="Pending Approvals"
              icon={<CheckCircle sx={{ color: THEME.gold, fontSize: 20 }} />}
              subtitle="Items waiting on your approval authority."
            >
              <Grid container spacing={2}>
                {visibleApprovals.map((item) => (
                  <Grid item xs={12} sm={6} md={3} key={item.key}>
                    <QueueTile
                      label={item.label}
                      value={item.value}
                      unit={item.unit}
                      accent={item.accent}
                    />
                  </Grid>
                ))}
              </Grid>
            </SectionCard>
          )}

          {/* Pending Actions */}
          {visibleActions.length > 0 && (
            <SectionCard
              title="Pending Actions"
              icon={<PendingActions sx={{ color: THEME.info, fontSize: 20 }} />}
              subtitle="Operational work that still needs execution."
            >
              <Grid container spacing={2}>
                {visibleActions.map((item) => (
                  <Grid item xs={12} sm={6} md={3} key={item.key}>
                    <QueueTile
                      label={item.label}
                      value={item.value}
                      unit={item.unit}
                      accent={item.accent}
                    />
                  </Grid>
                ))}
              </Grid>
            </SectionCard>
          )}

          {/* Store Alerts */}
          {canViewStore && storeAlertTiles.length > 0 && (
            <SectionCard
              title="Store Alerts"
              icon={<Inventory2 sx={{ color: THEME.warning, fontSize: 20 }} />}
              subtitle="Inventory and tooling signals that may affect readiness."
            >
              <Grid container spacing={2}>
                {storeAlertTiles.map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item.key}>
                    <QueueTile
                      label={item.label}
                      value={item.value}
                      unit={item.unit}
                      accent={item.accent}
                    />
                  </Grid>
                ))}
              </Grid>
            </SectionCard>
          )}

          {/* Recent + system alerts */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: `1px solid ${THEME.border}`,
                  bgcolor: THEME.card,
                  height: '100%'
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink, mb: 0.5 }}>
                  {isCrew ? 'Recent Assignments' : 'Recent Trips'}
                </Typography>
                <Typography variant="body2" sx={{ color: THEME.muted, mb: 1.5 }}>
                  {isCrew
                    ? 'Your latest crew assignments.'
                    : 'Most recent active trips in the pipeline.'}
                </Typography>
                <Divider sx={{ mb: 1, borderColor: THEME.border }} />
                <List disablePadding>
                  {recentFlights.length === 0 ? (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={isCrew ? 'No assignments found' : 'No trips found'}
                        primaryTypographyProps={{ sx: { color: THEME.muted } }}
                      />
                    </ListItem>
                  ) : (
                    recentFlights.map((trip) => (
                      <ListItem
                        key={trip._id}
                        divider
                        sx={{
                          px: 0,
                          borderColor: THEME.border,
                          '&:last-of-type': { borderBottom: 0 }
                        }}
                      >
                        <ListItemText
                          primary={`${trip.charterNumber || trip._id} — ${trip.title}`}
                          secondary={`${trip.flightType || 'CHARTER'}`}
                          primaryTypographyProps={{
                            sx: { color: THEME.ink, fontWeight: 600, fontSize: '0.95rem' }
                          }}
                          secondaryTypographyProps={{ sx: { color: THEME.muted } }}
                        />
                        <Chip
                          label={trip.status}
                          color={getStatusColor(trip.status)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: `1px solid ${THEME.border}`,
                  bgcolor: THEME.card,
                  height: '100%',
                  backgroundImage: `linear-gradient(160deg, ${alpha(THEME.navy, 0.03)} 0%, transparent 55%)`
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink, mb: 0.5 }}>
                  System Alerts
                </Typography>
                <Typography variant="body2" sx={{ color: THEME.muted, mb: 1.5 }}>
                  Readiness signals for your role.
                </Typography>
                <Divider sx={{ mb: 1, borderColor: THEME.border }} />
                {systemAlerts.length === 0 ? (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(THEME.success, 0.06),
                      border: `1px dashed ${alpha(THEME.success, 0.3)}`,
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="body2" sx={{ color: THEME.success, fontWeight: 600 }}>
                      No critical alerts
                    </Typography>
                    <Typography variant="caption" sx={{ color: THEME.muted }}>
                      Fleet and assignment signals look clear.
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {systemAlerts.map((alert) => {
                      const color =
                        alert.tone === 'danger'
                          ? THEME.danger
                          : alert.tone === 'info'
                            ? THEME.info
                            : THEME.warning;
                      return (
                        <ListItem
                          key={alert.key}
                          sx={{
                            px: 0,
                            py: 1.25,
                            alignItems: 'flex-start',
                            borderBottom: `1px solid ${THEME.border}`,
                            '&:last-of-type': { borderBottom: 0 }
                          }}
                        >
                          <Warning sx={{ color, mr: 1.25, mt: 0.25, fontSize: 20 }} />
                          <ListItemText
                            primary={alert.title}
                            secondary={alert.detail}
                            primaryTypographyProps={{
                              sx: { color: THEME.ink, fontWeight: 700, fontSize: '0.9rem' }
                            }}
                            secondaryTypographyProps={{ sx: { color: THEME.muted } }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
